const AUTH_SERVICE_URL = process.env.NEXT_PUBLIC_AUTH_SERVICE_URL || '/auth-service';

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export interface RegisterUserData {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  department?: string;
  location?: string;
}

export const authService = {
  async login(username: string, password: string): Promise<AuthResponse> {
    console.log('AuthService: Attempting login');
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await fetch(`/api/auth/generateToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    });

    if (!response.ok) {
      console.error('AuthService: Login failed', response.statusText);
      throw new Error('Login failed');
    }

    const data = await response.json();
    console.log('AuthService: Login successful, using ' + AUTH_SERVICE_URL);
    return data;
  },

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    console.log('AuthService: Attempting token refresh');
    const formData = new URLSearchParams();
    formData.append('refresh_token', refreshToken);

    const response = await fetch(`/api/auth/refreshToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    });

    if (!response.ok) {
      console.error('AuthService: Token refresh failed', response.statusText);
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    console.log('AuthService: Token refresh successful');
    return data;
  },

  async logout(token: string): Promise<void> {
    console.log('AuthService: Attempting logout');
    const response = await fetch(`/api/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      console.error('AuthService: Logout failed', response.statusText);
      throw new Error('Logout failed');
    }

    console.log('AuthService: Logout successful');
  },

  async register(userData: RegisterUserData): Promise<void> {
    console.log('AuthService: Attempting user registration');

    const response = await fetch(`/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: userData.username,
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        emailVerified: true,
        attributes: {
          department: [userData.department || 'General'],
          location: [userData.location || 'Unknown'],
        },
        enabled: false, // User is created but disabled
        credentials: [
          {
            type: 'password',
            temporary: false,
            value: userData.password,
          },
        ],
        disableableCredentialTypes: ['password'],
      }),
    });

    if (response.status === 400) {
      const errorData = await response.json();
      if (errorData.error && errorData.error_description === 'Account disabled') {
        console.warn('User created but is disabled.');
        alert('User has been created but is currently disabled.');
        return;
      }
      console.error('AuthService: Registration failed', errorData);
      throw new Error('Registration failed');
    }

    console.log('AuthService: Registration successful');
  },
};
